<template>
  <v-card flat class="px-3">
    <v-col
      cols="12"
      class="body-bold-smaller py-0"
    >
      <a type="primary" size="xsmall" @click="handleBack">
        <v-icon size="12" class="backIcon">mdi-arrow-left</v-icon>
        {{ $t('message.actions.return') }}
      </a>
    </v-col>

    <v-col class="pt-1 py-0">
      <span class="body-bold-base">
        {{ $t('message.pages.video.buttonsPage.title') }}
      </span>
    </v-col>
    <v-col cols="12" class="py-0">
      <span class="body-regular-smaller sub-text">
        {{ $t('message.pages.video.buttonsPage.addButtons') }}
        <a
          :href="$t('message.pages.video.buttonsPage.knowMoreHereURL')"
          target="_blank"
          type="secondary"
        >
          <span>{{ $t('message.pages.video.buttonsPage.knowMoreHere') }} </span>
          <v-icon size="12" style="vertical-align: middle; display: inline"
            >mdi-open-in-new</v-icon
          >
        </a>
      </span>
    </v-col>
    <v-col v-if="loading" cols="12">
      <v-row align="center" class="mt-5" justify="center">
        <v-progress-circular
          indeterminate
          color="primary"
          class="mb-4"
        ></v-progress-circular>
      </v-row>
    </v-col>
    <div v-else>
      <v-col class="pt-2 pl-3">
        <span
          style="display: flex; line-height: 24px; margin-top: 17px"
          class="body-bold-base"
        >
          {{ $t('message.pages.video.buttonsPage.videoButtons') }}
        </span>
        <span class="body-regular-smaller sub-text">
          {{ $t('message.pages.video.buttonsPage.videoButtonsExplain') }}
        </span>
      </v-col>

      <v-col
        cols="12"
        v-for="(cta, index) of ctaButtons"
        :key="`${cta.title}_${index}`"
      >
        <v-row
          style="
            display: flex;
            align-items: center;
            justify-content: space-between;
          "
          class="d-flex pl-3 mb-1"
        >
          <div style="display: grid">
            <span
              class="body-regular-small neutral-20-dark--text"
              style="display: flex; line-height: 24px"
              >{{
                cta.metadata.isCustomHtml
                  ? $t('message.pages.videoPlayerButtons.customizedHtml')
                  : cta.title
              }}</span
            >
            <span class="body-regular-smaller neutral-40-light--text">{{
              `${cta.metadata.startTime} - ${cta.metadata.endTime}`
            }}</span>
          </div>
          <v-spacer style="flex-grow: 100 !important" />
          <v-tooltip v-if="cta.id !== beingRemovedCtaId" top end>
            <template v-slot:activator="{ on, attrs }">
              <base-btn
                :disabled="!hasEditVideoPerm"
                size="xsmall"
                fontSize="small"
                type="neutral"
                class="d-flex panda-icons --buttons"
                @click="
                  () => {
                    $root.$emit('openModalInternalButton', cta)
                  }
                "
                v-on="on"
                v-bind="attrs"
              >
                <v-icon color="var(--neutral-40-ligth)" size="16"
                  >panda-edit</v-icon
                >
              </base-btn>
            </template>
            <span>{{ $t('message.edit') }}</span>
          </v-tooltip>
          <v-menu
            v-if="cta.id !== beingRemovedCtaId"
            offset-y
            bottom
            left
            :close-on-content-click="false"
            class="ml-2"
            min-width="140px"
          >
            <template v-slot:activator="{ on, attrs }">
              <base-btn
                :disabled="!hasEditVideoPerm"
                color="neutral-light"
                dark
                v-bind="attrs"
                v-on="on"
                class="ml-2 panda-icons --buttons"
                icon
                type="neutral"
                style="
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  font-size: 16px;
                  padding: 0 !important;
                  width: 30px !important;
                  height: 30px !important;
                "
              >
                <v-icon class="icon" size="16px">mdi-dots-vertical</v-icon>
              </base-btn>
            </template>
            <v-list class="list__subtitle-actions" dense>
              <v-list-item
                @click="handleRemoveCta(cta, index)"
                style="cursor: pointer"
                :disabled="!hasEditVideoPerm"
              >
                <v-list-item-title
                  class="feedback-danger--text"
                  style="color: red !important"
                >
                  <span class="body-regular-smaller">{{
                    $t('message.remove')
                  }}</span>
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <div v-else class="d-flex align-center">
            <v-progress-circular
              indeterminate
              :size="16"
              :width="3"
              color="brand"
            ></v-progress-circular>
          </div>
        </v-row>
      </v-col>

      <v-col class="py-0" :class="ctaButtons.length > 0 ? '' : 'pt-4'">
        <base-btn
          @click="
            () => {
              $root.$emit('openModalInternalButton')
            }
          "
          :disabled="!hasEditVideoPerm"
          type="secondary"
          size="small"
        >
          <v-icon class="mx-0 mr-1">mdi-plus</v-icon>
          {{ $t('message.pages.video.buttonsPage.addButton') }}
        </base-btn>
      </v-col>
      <v-col cols="12">
        <v-divider class="my-5"></v-divider>
      </v-col>
      <v-col class="py-0 pl-3">
        <span style="display: flex; line-height: 10px" class="body-bold-base">
          {{ $t('message.pages.video.buttonsPage.addOutsideButton') }}
        </span>
        <span class="body-regular-smaller sub-text">
          {{ $t('message.pages.video.buttonsPage.addOutsideButtonExplain') }}
        </span>
      </v-col>

      <v-col
        cols="12"
        class="mt-3"
        v-for="(cta, index) of outsideCtaButtons"
        :key="`${cta.title}_${index}`"
      >
        <v-row
          style="
            display: flex;
            align-items: center;
            justify-content: space-between;
          "
          class="d-flex pl-3 mb-1"
        >
          <div style="display: grid">
            <span
              class="body-regular-small neutral-20-dark--text"
              style="display: flex; line-height: 24px"
              >{{
                cta.metadata.isCustomHtml
                  ? $t('message.pages.videoPlayerButtons.customizedHtml')
                  : cta.title
              }}</span
            >
            <span
              class="body-regular-small neutral-40-light--text"
              style="font-size: 12px"
              >{{ `${cta.metadata.startTime} - ${cta.metadata.endTime}` }}</span
            >
          </div>
          <v-spacer />
          <base-btn
            :disabled="!hasEditVideoPerm"
            v-if="cta.id !== beingRemovedCtaId"
            type="tertiary"
            size="small"
            class="mr-6"
            @click="copyElementButton(cta)"
            ><v-icon medium class="mr-1" style="padding: revert"
              >mdi-code-tags
            </v-icon>
            {{ $t('message.pages.video.buttonsPage.copy') }}
          </base-btn>
          <v-tooltip v-if="cta.id !== beingRemovedCtaId" top>
            <template v-slot:activator="{ on, attrs }">
              <base-btn
                :disabled="!hasEditVideoPerm"
                type="neutral"
                size="xsmall"
                fontSize="small"
                class="d-flex justify-center panda-icons --buttons"
                style="cursor: pointer"
                @click="
                  () => {
                    $root.$emit('openModalExternalButton', cta)
                  }
                "
                v-on="on"
                v-bind="attrs"
              >
                <v-icon color="var(--neutral-40-ligth)" size="16"
                  >panda-edit</v-icon
                >
              </base-btn>
            </template>
            <span>{{ $t('message.edit') }}</span>
          </v-tooltip>
          <v-menu
            v-if="cta.id !== beingRemovedCtaId"
            offset-y
            bottom
            left
            :close-on-content-click="false"
            class="ml-2"
            min-width="140px"
          >
            <template v-slot:activator="{ on, attrs }">
              <base-btn
                :disabled="!hasEditVideoPerm"
                color="neutral-light"
                dark
                v-bind="attrs"
                v-on="on"
                class="ml-2 panda-icons --buttons"
                icon
                type="neutral"
                style="
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  font-size: 16px;
                  padding: 0 !important;
                  width: 30px !important;
                  height: 30px !important;
                "
              >
                <v-icon class="icon" size="16px">mdi-dots-vertical</v-icon>
              </base-btn>
            </template>
            <v-list dense class="list__subtitle-actions">
              <v-list-item
                @click="handleRemoveCta(cta, index)"
                style="cursor: pointer"
              >
                <v-list-item-title
                  class="feedback-danger--text"
                  style="color: red !important"
                >
                  <span class="body-regular-smaller">{{
                    $t('message.remove')
                  }}</span>
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <div v-else class="d-flex align-center">
            <v-progress-circular
              indeterminate
              :size="16"
              :width="3"
              color="brand"
            ></v-progress-circular>
          </div>
        </v-row>
      </v-col>

      <v-col class="py-0" :class="outsideCtaButtons.length > 0 ? '' : 'pt-4'">
        <base-btn
          :disabled="!hasEditVideoPerm"
          @click="
            () => {
              $root.$emit('openModalExternalButton', null)
            }
          "
          type="secondary"
          size="small"
        >
          <v-icon class="mx-0 mr-1">mdi-plus</v-icon>
          {{ $t('message.pages.video.buttonsPage.addButton') }}
        </base-btn>
      </v-col>
      <portal to="actions-footer">
        <div class="d-flex flex-row actions-row">
          <v-spacer></v-spacer>
          <v-tooltip max-width="184px" top :disabled="false">
            <template v-slot:activator="{ on, attrs }">
              <div class="pl-2" v-bind="attrs" v-on="on">
                <base-btn
                  type="tertiary"
                  class="mr-2 px-4"
                  text
                  :disabled="false"
                  @click="() => {}"
                >
                  {{ $t('message.cancel') }}
                </base-btn>
              </div>
            </template>
            <span>{{ $t('message.noChanges') }}</span>
          </v-tooltip>
          <v-tooltip max-width="184px" top :disabled="false">
            <template v-slot:activator="{ on, attrs }">
              <div class="pl-2" v-bind="attrs" v-on="on">
                <base-btn
                  type="primary"
                  :loading="false"
                  :disabled="false"
                  width="150"
                  @click="() => {}"
                >
                  <span class="px-5">{{
                    $t('message.pages.video.publish')
                  }}</span>
                </base-btn>
              </div>
            </template>
            <span>{{ $t('message.noChanges') }}</span>
          </v-tooltip>
        </div>
      </portal>
      <video-player-copy-external-btn
        :showMenuEmbed="showCopyEmbed"
        :fromVideoListing="false"
        :fetching="false"
        :video="video"
        :selectedCta="selectedCta"
        :openModal="openModal"
        @update:openModal="updateOpenCopyModal"
      />
      <video-player-add-button :handleUpdateVideo="handleUpdateVideo" />
      <video-player-add-external-button
        :handleUpdateVideo="handleUpdateVideo"
      />
    </div>
  </v-card>
</template>
<script>
import { mapMutations, mapState, mapGetters } from 'vuex'
import playerApi from '@/services/playerApi'
import VideoPlayerAddButton from './VideoPlayerAddButton.vue'
import VideoPlayerAddExternalButton from './VideoPlayerAddExternalButton.vue'
import VideoPlayerCopyExternalBtn from './VideoPlayerCopyExternalBtn.vue'

export default {
  name: 'VideoPlayerButtons',
  components: {
    VideoPlayerAddButton,
    VideoPlayerAddExternalButton,
    VideoPlayerCopyExternalBtn,
  },
  props: {
    handleBack: {
      type: Function,
      required: true,
    },
    hasEdit: {
      type: Array,
    },
  },
  data: () => ({
    timeVideo: null,
    buttonType: 'video',
    ctaButtons: [],
    selectedCta: {},
    outsideCtaButtons: [],
    textColorMenu: false,
    textHoverMenu: false,
    backgroundColorMenu: false,
    backgroundHoverMenu: false,
    openModal: false,
    scrollInvoked: 0,
    beingRemovedCtaId: '',
    beingEditedCtaId: '',
    loadings: {
      creatingCta: false,
      updatingCta: false,
      removingCta: false,
    },
    error: '',
    wasSomethingChanged: false,
    initialEditingCtaValue: {},
    initialAppendWebsite: false,
    loading: false,
    editingButton: {
      title: '',
      link: '',
      startTime: '00:00',
      endTime: '00:05',
      selectedButtonType: 'underVideo',
      textColor: '#FFFFFF',
      textHover: '#FFFFFF',
      backgroundColor: '#3e50f7',
      backgroundHover: '#3e50f7',
      selectedButtonSize: 'small',
      selectedButtonPlacement: 'bottomCenter',
      isCustomHtml: false,
      codeHtml: '',
      lastAllVideo: false,
      lastTillEnd: false,
      keepWindow: false,
      appendWebsiteQueryParams: false,
    },
    showCopyEmbed: false,
  }),
  computed: {
    ...mapState({
      video: (state) => state.video.video,
    }),
    allCtas() {
      return this.ctaButtons.concat(this.outsideCtaButtons)
    },
    ...mapGetters('user', ['hasEditVideoPerm']),
    allCtasVideo() {
      return this.allCtas.filter(
        (cta) =>
          cta.type !== 'end' &&
          cta.type !== 'pause' &&
          !cta?.metadata?.isMiniTrigger
      )
    },
    knowMoreURL() {
      return 'https://help.pandavideo.com/pt-br/article/como-inserir-botoes-de-redirecionamento-no-video-com-o-player-da-panda-jpzfob/'
    },
  },
  mounted() {
    this.editingButton.title = this.$t('message.pages.video.buttonDefaultText')

    this.timeVideo = this.$store.state.video.video?.length
    this.$root.$on('discardChangesBtnPage', () => {
      this.wasSomethingChanged = false
      this.setConfigCtas()
      this.handleGetBack()
    })
    this.setConfigCtas()
    if (this.isThumbButton) {
      this.editingButton.selectedButtonType = 'insideVideo'
    }
    // this.$root.$on('getPauseThumbButton', () => {
    //   this.handleThumbnailButton()
    // })
    // this.$root.$on('editPauseThumbButton', (buttonConfig, pauseThumb = '') => {
    //   this.editingButton = buttonConfig
    //   this.fixAppendWebsiteVal()
    //   this.pauseThumbUrl = pauseThumb
    //   this.buttonType = 'pause'
    //   this.setIframeConfig()
    // })
    this.isEditing = this.allCtasVideo.length === 0
    this.hasEdit.push(this.wasSomethingChanged)
  },
  methods: {
    ...mapMutations('video', ['UPDATE_VIDEO']),
    ...mapMutations('user', ['UPDATE_GLOBAL_SNACKBAR']),
    updateOpenCopyModal(value) {
      this.openModal = value
    },
    async handleUpdateVideo() {
      this.loading = true
      try {
        const ret = await playerApi.getVideoCurrentConfig(this.video.id)
        this.video.config = ret.data

        this.ctaButtons = ret.data.ctas
          ? ret.data.ctas.filter(
              (cta) => cta.metadata && 'selectedButtonType' in cta.metadata
            )
          : []
        this.outsideCtaButtons = ret.data.outsideCta
          ? ret.data.outsideCta.filter(
              (cta) => cta.metadata && 'selectedButtonType' in cta.metadata
            )
          : []
      } catch (error) {
        console.log('🚀 ~ handleUpdateVideo ~ error:', error)
      }
      this.loading = false
    },
    async handleRemoveCta(cta) {
      try {
        this.beingRemovedCtaId = cta.id
        let ret
        if (cta.metadata.selectedButtonType === 'underVideo') {
          ret = await playerApi.deleteOutsideCta(this.video.id, cta.id)
        } else {
          ret = await playerApi.deleteCta(this.video.id, cta.id)
        }
        this.video.config = ret.data

        this.UPDATE_VIDEO(this.video)
        this.ctaButtons =
          (ret.data.ctas || []).filter(
            (cta) => cta.metadata && 'selectedButtonType' in cta.metadata
          ) || []
        this.outsideCtaButtons = ret.data.outsideCta
          ? ret.data.outsideCta.filter(
              (cta) => cta.metadata && 'selectedButtonType' in cta.metadata
            )
          : []

        this.error = ''
        this.isEditing = this.allCtasVideo.length === 0
        this.UPDATE_GLOBAL_SNACKBAR({
          open: true,
          text: this.$t('message.pages.video.buttonsPage.toastRemovedButton'),
          type: 'success',
        })
      } catch (error) {
        this.error = this.$t(
          'message.pages.video.buttonsPage.errors.removeError'
        )
        console.log(error)
      }
      this.$root.$emit('updateEmbedPreviewChange')
    },
    copyElementButton(cta) {
      this.selectedCta = cta
      this.openModal = true
    },
    setConfigCtas() {
      // Ifs com variavel .textButtonColor para verificar a existência do metadata usado pelo layout antigo.
      let videoCtas = this.video?.config?.ctas ?? []
      videoCtas = videoCtas.filter(
        (cta) => cta.metadata && 'selectedButtonType' in cta.metadata
      )
      if (videoCtas[0]?.metadata?.textButtonColor) {
        const mappedCtas = videoCtas.map((cta) => {
          return {
            ...cta,
            metadata: {
              title: cta.title,
              link: cta.metadata.link,
              startTime: this.formatSecondsToTime(cta.time),
              endTime: this.formatSecondsToTime(cta.time + cta.duration),
              selectedButtonType: cta.metadata.isOutsideButton
                ? 'underVideo'
                : 'insideVideo',
              textColor: cta.metadata.textButtonColor,
              textHover: cta.metadata.textButtonColor,
              backgroundColor: cta.metadata.buttonColor,
              backgroundHover: cta.metadata.buttonColor,
              selectedButtonSize: 'small',
              selectedButtonPlacement: 'bottomCenter',
              isCustomHtml: false,
              codeHtml: '',
              lastAllVideo: cta.metadata.lastAllVideo || false,
              lastTillEnd: cta.metadata.lastTillEnd || false,
              keepWindow: cta.metadata.keepWindow || false,
            },
          }
        })
        this.ctaButtons = mappedCtas
      } else {
        videoCtas = videoCtas.filter((el) => el.type === 'video')
        const fixedButtons = videoCtas.map((cta) => {
          return {
            ...cta,
            metadata: {
              ...cta.metadata,
              startTime: this.formatSecondsToTime(cta.time),
              endTime: this.formatSecondsToTime(cta.time + cta.duration),
              selectedButtonType: 'insideVideo',
            },
          }
        })
        this.ctaButtons = fixedButtons
      }
      let videoOutsideCtas = this.video?.config?.outsideCta ?? []
      if (videoOutsideCtas[0]?.metadata?.textButtonColor) {
        const outsideMappedCtas = videoOutsideCtas.map((cta) => {
          return {
            ...cta,
            metadata: {
              title: cta.title,
              link: cta.metadata.link,
              startTime: this.formatSecondsToTime(cta.time),
              endTime: this.formatSecondsToTime(cta.time + cta.duration),
              selectedButtonType: cta.metadata.isOutsideButton
                ? 'underVideo'
                : 'insideVideo',
              textColor: cta.metadata.textButtonColor,
              textHover: cta.metadata.textButtonColor,
              backgroundColor: cta.metadata.buttonColor,
              backgroundHover: cta.metadata.buttonColor,
              selectedButtonSize: 'small',
              selectedButtonPlacement: 'bottomCenter',
              isCustomHtml: cta.metadata.enableCustomHtml,
              codeHtml: cta.metadata.enableCustomHtml ? cta.script : '',
              lastAllVideo: cta.metadata.lastAllVideo || false,
              lastTillEnd: cta.metadata.lastTillEnd || false,
              keepWindow: cta.metadata.keepWindow || false,
            },
          }
        })
        this.outsideCtaButtons = outsideMappedCtas
      } else {
        let fixedButtons = videoOutsideCtas.map((cta) => {
          return {
            ...cta,
            metadata: {
              ...cta.metadata,
              startTime: this.formatSecondsToTime(cta.time),
              endTime: this.formatSecondsToTime(cta.time + cta.duration),
              selectedButtonType: 'underVideo',
            },
          }
        })
        this.outsideCtaButtons = fixedButtons
      }
    },
    formatSecondsToTime(seconds) {
      let res = new Date(seconds * 1000).toISOString()
      if (this.timeVideo < 3600) {
        return res.substring(14, 19)
      }
      return res.substring(11, 19)
    },
  },
}
</script>
<style scoped lang="scss">
.base-button.panda-icons.--buttons {
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  width: 32px !important;
  padding: 3px;
  height: 32px;
}
.backIcon {
  color: varFigma(brand-60-base) !important;
}
.redirectLink {
  color: varFigma(neutral-30-base) !important;
}
.copyBtn {
  margin-left: auto;
  cursor: pointer;
  background: varFigma(neutral-95);
  color: var(neutral-30-base);
}
.sub-text {
  color: varFigma(neutral-40-light) !important;
}
.buttonTitle {
  color: varFigma(neutral-20-dark);
  font-size: 14px;
}
.buttonTime {
  color: varFigma(neutral-40-light);
  font-size: 12px;
}
.icon {
  color: varFigma(neutral-40-light) !important;
  background-color: unset !important;
}
</style>
